import { c as _c } from "react/compiler-runtime";
import type { HofDashboardAnswer, HofDashboardFile } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { formatFileSize, formatNumber } from '@/features/hof-dashboard/texts';
import type { Locale } from '@/types/types';
import { FileText } from 'lucide-react';
import type React from 'react';

/** The ending of a file name in capitals, "PDF", as a short type next to its size. */
const fileType = (name: string): string | undefined => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : undefined;
};

/**
 * A file handed in, as the form's upload lists it: name, type and size, the row opening it.
 */
export const FileRow: React.FC<{
  file: HofDashboardFile;
  locale: Locale;
}> = t0 => {
  const $ = _c(19);
  const {
    file,
    locale
  } = t0;
  let t1;
  if ($[0] !== file.name) {
    t1 = fileType(file.name);
    $[0] = file.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== file.size || $[3] !== locale) {
    t2 = file.size === undefined ? undefined : formatFileSize(file.size, locale);
    $[2] = file.size;
    $[3] = locale;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = [t1, t2].filter(_temp);
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const details = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FileText className="h-5 w-5 shrink-0 text-gray-400" aria-hidden={true} />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== file.name) {
    t5 = <span className="font-body block truncate text-sm font-medium text-gray-700 underline-offset-2 hover:underline">{file.name}</span>;
    $[9] = file.name;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== details) {
    t6 = details.length > 0 && <span className="block text-xs text-gray-400">{details.join(" \xB7 ")}</span>;
    $[11] = details;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <span className="min-w-0">{t5}{t6}</span>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== file.url || $[17] !== t7) {
    t8 = <li><a href={file.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-md border border-gray-100 bg-white p-3 shadow-xs focus-visible:outline-2 focus-visible:outline-green-600">{t4}{t7}</a></li>;
    $[16] = file.url;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};

/** Ordered materials by section, with their quantities right-aligned. */
const MaterialTable: React.FC<{
  materials: Extract<HofDashboardAnswer, {
    kind: 'materials';
  }>['materials'];
  locale: Locale;
}> = t0 => {
  const $ = _c(5);
  const {
    materials,
    locale
  } = t0;
  let sections;
  if ($[0] !== materials) {
    sections = [];
    for (const line of materials) {
      const last = sections.at(-1);
      if (last !== undefined && last.section === line.section) {
        last.lines.push(line);
      } else {
        sections.push({
          section: line.section,
          lines: [line]
        });
      }
    }
    $[0] = materials;
    $[1] = sections;
  } else {
    sections = $[1];
  }
  let t1;
  if ($[2] !== locale || $[3] !== sections) {
    t1 = <div className="space-y-3">{sections.map((t2, index) => {
        const {
          section,
          lines
        } = t2;
        return <div key={section ?? `section-${index}`}>{section !== undefined && <p className="font-body mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{section}</p>}<ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">{lines.map(line_0 => <li key={line_0.id} className="flex items-baseline justify-between gap-3 px-3 py-2"><span className="text-sm text-gray-700">{line_0.name}</span><span className="text-sm font-semibold text-gray-900 tabular-nums">{formatNumber(line_0.quantity, locale)}</span></li>)}</ul></div>;
      })}</div>;
    $[2] = locale;
    $[3] = sections;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

/** What a submission answered, in the order its form asks, labelled as in the form. */
export const EntryAnswers: React.FC<{
  answers: HofDashboardAnswer[];
  locale: Locale;
}> = t0 => {
  const $ = _c(7);
  const {
    answers,
    locale
  } = t0;
  let t1;
  if ($[0] !== answers || $[1] !== locale) {
    let t2;
    if ($[3] !== locale) {
      t2 = answer => <div key={answer.field}><dt className="font-body mb-1 text-sm font-medium text-gray-500">{answer.label}</dt><dd>{answer.kind === "text" && <p className="text-sm whitespace-pre-line text-gray-900">{answer.text}</p>}{answer.kind === "files" && <ul className="space-y-2">{answer.files.map(file => <FileRow key={file.id} file={file} locale={locale} />)}</ul>}{answer.kind === "materials" && <MaterialTable materials={answer.materials} locale={locale} />}</dd></div>;
      $[3] = locale;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = answers.map(t2);
    $[0] = answers;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <dl className="space-y-4">{t1}</dl>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
function _temp(part) {
  return part !== undefined;
}